import {
	pgTable,
	timestamp,
	text,
	date,
	pgEnum,
	jsonb,
	uuid,
	boolean,
	integer,
	index,
	uniqueIndex,
	primaryKey
} from "drizzle-orm/pg-core";
import type { AnyPgColumn } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

import { baseTableProps } from "../../baseTableProps";
import { users } from "../users/users.model";
import type { DisplayNameMeta } from "../_shared/display-name";

import type {
	AboutMe,
	AboutMeEntry,
	HeadshotMeta,
	MemberProvisioning
} from "@fergmar/contracts/members";

export const statusEnum = pgEnum("status_enum", ["Active", "Inactive"]);
export const locationTypeEnum = pgEnum("location_type", ["Office", "Field"]);

type ScreeningKindRecord = {
	status: "not_started" | "in_progress" | "cleared" | "failed";
	date: string | null;
};

export type OnboardingData = {
	screening?: {
		backgroundCheck: ScreeningKindRecord;
		drugScreen: ScreeningKindRecord;
		i9: ScreeningKindRecord;
		orders: unknown[];
	};
	backgroundCheck?: ScreeningKindRecord;
	sourcePendingId: string | null;
	[key: string]: unknown;
};

export const teamMembers = pgTable(
	"team_members",
	{
		...baseTableProps,
		firstName: text("first_name").notNull(),
		preferredName: text("preferred_name").notNull(),
		middleName: text("middle_name"),
		lastName: text("last_name").notNull(),
		title: text("title").notNull(),
		level: text("level"),
		headshot: text("headshot")
			.notNull()
			.default("https://cdn.fergmar.com/static/associates/headshots/default_avatar.webp"),
		status: statusEnum("status").notNull(),
		locationType: locationTypeEnum("location_type").notNull(),
		hireDate: date("hire_date").notNull(),
		birthDate: date("birth_date"),
		userId: text("user_id")
			.unique("team_members_user_id_unique")
			.references(() => users.id, {
				name: "team_members_user_id_users_id_fk",
				onDelete: "set null"
			}),
		officeLine: text("office_line").default("(361) 582-4663"),
		workNumber: text("work_number").default("null"),
		workEmail: text("work_email").notNull(),
		piProfile: text("pi_profile"),
		personalNumber: text("personal_number"),
		personalEmail: text("personal_email"),
		bio: text("bio"),
		onboardingData: jsonb("onboarding_data")
			.$type<OnboardingData>()
			.$default(() => ({
				screening: {
					backgroundCheck: { status: "not_started", date: null },
					drugScreen: { status: "not_started", date: null },
					i9: { status: "not_started", date: null },
					orders: []
				},
				sourcePendingId: null
			})),
		displayName: text("display_name")
			.generatedAlwaysAs(sql`COALESCE(preferred_name, first_name) || ' ' || last_name`)
			.notNull(),
		initials: text("initials")
			.generatedAlwaysAs(
				sql`UPPER(LEFT(COALESCE(preferred_name, first_name), 1) ||               
	LEFT(last_name, 1))`
			)
			.notNull(),
		legalName: text("legal_name")
			.generatedAlwaysAs(
				sql`
  CASE
	  WHEN middle_name IS NULL THEN first_name || ' ' || last_name
	  ELSE first_name || ' ' || middle_name || ' ' || last_name
  END`
			)
			.notNull(),
		reportsToId: uuid("reports_to_id").references((): AnyPgColumn => teamMembers.id, {
			name: "team_members_reports_to_id_team_members_id_fk"
		}),
		meetingLink: text("meeting_link"),
		aboutMe: jsonb("about_me").$type<AboutMe | AboutMeEntry[]>().default([]),
		displaySites: text("display_sites").array().default([]),
		teams: text("teams").array().default([]),
		provisioning: jsonb("provisioning").$type<MemberProvisioning>(),
		headshotMeta: jsonb("headshot_meta").$type<HeadshotMeta>(),
		displayNameMeta: jsonb("display_name_meta").$type<DisplayNameMeta>()
	},
	(table) => [
		index("team_members_reports_to_id_idx").on(table.reportsToId),
		uniqueIndex("team_members_active_work_email_unique")
			.on(sql`lower(trim(${table.workEmail}))`)
			.where(sql`${table.status} = 'Active'`)
	]
);

export const teamMemberMedia = pgTable(
	"team_member_media",
	{
		...baseTableProps,
		id: uuid("id").notNull().defaultRandom(),
		softDeleted: boolean("soft_deleted").notNull().default(false),
		softDeletedAt: timestamp("soft_deleted_at", { withTimezone: true, mode: "string" }),
		teamMemberId: uuid("team_member_id").references(() => teamMembers.id, {
			name: "team_member_media_team_member_id_team_members_id_fk",
			onDelete: "set null"
		}),
		bucketId: text("bucket_id").notNull(),
		key: text("key").notNull(),
		contentType: text("content_type").notNull(),
		contentLength: integer("content_length").notNull(),
		displayName: text("display_name").notNull().default(""),
		fileId: text("file_id").notNull().default(""),
		additionalMetadata: jsonb("additional_metadata").$type<Record<string, unknown>>()
	},
	(table) => [primaryKey({ name: "team_member_files_pkey", columns: [table.id] })]
);
